import { createFileRoute, Link } from "@tanstack/react-router";
import { Check } from "lucide-react";
import { buildHead } from "@/lib/seo";
import { Logo } from "@/components/site/Logo";
import { LeadCaptureForm } from "@/components/site/LeadCaptureForm";
import { setEdgeHtmlCache } from "@/lib/cache-headers";

const PATH = "/es/lp/agencia-shopify";
const SOURCE = "lp-shopify-agency-es";

const FAQS = [
  { q: "¿Qué tan rápido podemos arrancar?", a: "Dentro de 5 días hábiles del depósito. La mayoría de diagnósticos se cotizan en 48 horas tras tu formulario." },
  { q: "¿Qué incluye el diagnóstico?", a: "Un engagement pagado de 2 semanas: auditoría de performance, revisión de conversión, gap de capacidades Plus y roadmap priorizado a 90 días. El entregable es tuyo." },
  { q: "¿Solo trabajan con Shopify Plus?", a: "Trabajamos en Shopify y Shopify Plus. La mayoría involucra Plus (Functions, B2B, Markets), pero proyectos Shopify estándar son bienvenidos." },
  { q: "¿Cómo son los precios?", a: "Diagnósticos desde $2,500. Builds típicamente entre $15K–$75K. Los retainers de ingeniería Shopify Plus comienzan en $9,000 USD al mes para marcas que requieren soporte senior continuo en Functions, B2B, Markets, integraciones, releases complejos y arquitectura técnica. Confirmamos alcance y precio antes de cualquier compromiso." },
  { q: "¿Con quién vamos a trabajar?", a: "Un ingeniero senior + project lead, ambos Shopify-certified. Sin handoffs offshore, sin junior account managers." },
];

export const Route = createFileRoute("/es/lp/agencia-shopify")({
  loader: async () => { await setEdgeHtmlCache(); },
  head: () =>
    buildHead({
      locale: "es",
      title: "Agencia Shopify para marcas DTC serias: Shugert",
      description:
        "Habla con un Shopify Select Partner. Diagnóstico, alcance y kickoff en 5 días. Performance, conversión e ingeniería Plus para marcas DTC establecidas.",
      path: PATH,
      altPath: "/lp/shopify-agency",
      noindex: true,
    }),
  component: LpAgenciaShopifyPage,
});

function LpAgenciaShopifyPage() {
  return (
    <div className="flex min-h-dvh flex-col bg-background text-foreground">
      <header className="border-b border-border">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-5">
          <Link to="/es" className="inline-flex items-center" aria-label="Shugert, inicio">
            <Logo className="h-10 w-auto" />
          </Link>
          <a
            href="#lead-form-repeat"
            className="hidden text-sm font-medium text-foreground hover:opacity-80 sm:inline"
          >
            Pedir diagnóstico →
          </a>
        </div>
      </header>

      <main className="flex-1">
        <section className="mx-auto max-w-6xl px-6 py-16 sm:py-20">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-2 lg:gap-16">
            <div>
              <p className="text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">
                Shopify Select Partner · Desde 2015
              </p>
              <h1 className="mt-4 text-balance text-4xl font-semibold leading-[1.05] text-foreground sm:text-5xl">
                El partner Shopify que las marcas DTC serias sí conservan.
              </h1>
              <p className="mt-6 text-lg leading-relaxed text-muted-foreground">
                Performance, conversión e ingeniería Plus, hecho por ingenieros senior, cotizado antes de comprometerte, kickoff en 5 días hábiles.
              </p>
              <ul className="mt-8 space-y-3">
                {[
                  "Diagnóstico entregado en 2 semanas, es tuyo.",
                  "Alcance a precio fijo. Sin facturas sorpresa.",
                  "Solo ingenieros senior. Sin handoffs offshore.",
                ].map((x) => (
                  <li key={x} className="flex items-start gap-3 text-sm text-foreground">
                    <Check className="mt-0.5 h-5 w-5 flex-none text-emerald-500" aria-hidden="true" />
                    {x}
                  </li>
                ))}
              </ul>
              <div className="mt-10 grid grid-cols-2 gap-4 text-sm text-muted-foreground sm:grid-cols-4">
                <div><strong className="text-foreground block">750+</strong> proyectos</div>
                <div><strong className="text-foreground block">35</strong> mercados</div>
                <div><strong className="text-foreground block">2015</strong> fundado</div>
                <div><strong className="text-foreground block">Select</strong> Partner</div>
              </div>
            </div>

            <div id="lead-form-hero">
              <LeadCaptureForm locale="es" source={SOURCE} variant="hero" />
            </div>
          </div>
        </section>

        <section className="border-t border-border bg-muted/20">
          <div className="mx-auto max-w-6xl px-6 py-16">
            <h2 className="text-2xl font-semibold text-foreground sm:text-3xl">Qué recibes</h2>
            <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-3">
              {[
                { t: "Diagnóstico en 2 semanas", d: "Auditoría de performance, conversión y capacidades Plus. Roadmap priorizado a 90 días con estimaciones de esfuerzo + impacto en revenue." },
                { t: "Plan cotizado, precio fijo", d: "Compartimos alcance y precio antes de comprometerte. Sin retainer obligatorio. Proyectos típicos $15K–$75K con antes/después claro." },
                { t: "Kickoff en 5 días", d: "Del formulario a la primera llamada de ingeniería en una semana hábil. La mayoría de diagnósticos cierra en 90 días." },
              ].map((x) => (
                <div key={x.t} className="rounded-2xl border border-border bg-card p-6">
                  <h3 className="font-semibold text-foreground">{x.t}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{x.d}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="border-t border-border">
          <div className="mx-auto max-w-6xl px-6 py-16">
            <h2 className="text-2xl font-semibold text-foreground sm:text-3xl">Marcas para las que hemos enviado</h2>
            <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-3">
              {[
                { metric: "+38%", label: "lift en conversión móvil", note: "Apparel DTC, 6 semanas" },
                { metric: "1.1s", label: "LCP tras rebuild", note: "Marca beauty Plus, antes 3.8s" },
                { metric: "$2.1M", label: "revenue incremental / año", note: "Functions + checkout extensibility" },
              ].map((x) => (
                <div key={x.label} className="rounded-2xl border border-border bg-card p-6">
                  <p className="text-3xl font-semibold text-foreground sm:text-4xl">{x.metric}</p>
                  <p className="mt-2 text-sm font-medium text-foreground">{x.label}</p>
                  <p className="mt-1 text-xs uppercase tracking-wider text-muted-foreground">{x.note}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="border-t border-border bg-muted/20">
          <div className="mx-auto max-w-3xl px-6 py-16">
            <h2 className="text-2xl font-semibold text-foreground sm:text-3xl">Preguntas comunes</h2>
            <div className="mt-8 space-y-4">
              {FAQS.map((f) => (
                <details key={f.q} className="group rounded-2xl border border-border bg-card p-5">
                  <summary className="flex cursor-pointer items-center justify-between text-base font-medium text-foreground">
                    {f.q}
                    <span className="ml-4 text-muted-foreground transition-transform group-open:rotate-45">+</span>
                  </summary>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="border-t border-border">
          <div className="mx-auto max-w-2xl px-6 py-16">
            <div className="text-center">
              <h2 className="text-2xl font-semibold text-foreground sm:text-3xl">
                ¿Listo para cotizar tu proyecto?
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                Llena el formulario, respondemos en 48 horas, normalmente el mismo día.
              </p>
            </div>
            <div className="mt-10" id="lead-form-repeat">
              <LeadCaptureForm locale="es" source={SOURCE} variant="repeat" />
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t border-border">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-3 px-6 py-8 text-xs text-muted-foreground sm:flex-row">
          <p>© {new Date().getFullYear()} Shugert Agency. Todos los derechos reservados.</p>
          <nav className="flex items-center gap-5">
            <Link to="/es/privacidad" className="hover:text-foreground">Privacidad</Link>
            <Link to="/es/terminos" className="hover:text-foreground">Términos</Link>
            <a href="mailto:hello@shugert.com.mx" className="hover:text-foreground">hello@shugert.com.mx</a>
          </nav>
        </div>
      </footer>
    </div>
  );
}